import { Scenario } from '../../domain/index.js';
import { validateScenario } from '../../domain/validators.js';
import { isScenarioShape } from './storage.js';

export type ShareErrorCode = 'unsupported' | 'too-long' | 'corrupt' | 'not-a-scenario';

/** Every failure of the codec; `code` is what the UI translates. */
export class ShareError extends Error {
  readonly code: ShareErrorCode;

  constructor(code: ShareErrorCode) {
    super(`Share token: ${code}`);
    this.name = 'ShareError';
    this.code = code;
  }
}

/** Fragment parameter that carries the token: `#s=<token>`. */
export const SHARE_PARAM = 's';
/** Format version; bump when the payload or the compression changes. */
export const TOKEN_PREFIX = 'v1.';
/** Calendar payloads use v2 so older builds reject them instead of calculating monthly interest. */
export const CALENDAR_TOKEN_PREFIX = 'v2.';
/** Tokens longer than this are rejected before decoding. */
export const MAX_SHARE_PAYLOAD_LENGTH = 16 * 1024;

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new ShareError('corrupt');
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4 || 4)) % 4);
  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    throw new ShareError('corrupt');
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

type Raw = Record<string, unknown>;

/** Same limit as the name input in the form (`maxlength="48"`). */
const NAME_MAX_LENGTH = 48;
const SCENARIO_KEYS = ['name', 'principal', 'termMonths', 'rateApr', 'repaymentType', 'startDate', 'overpaymentStrategy', 'calculationMode'];
const BANK_CALENDAR_KEYS = ['disbursementDate', 'paymentDay', 'recalculationMonths'];
const RATE_PERIOD_KEYS = ['startMonth', 'endMonth', 'rateApr'];
const OVERPAYMENT_KEYS = ['month', 'amount', 'strategy'];
const RECURRING_KEYS = ['endMonth', 'step'];
const ADAPTIVE_KEYS = ['targetAmount', 'minAmount', 'endMonth', 'step'];
const REFINANCE_KEYS = ['month', 'newRateApr', 'newTermMonths', 'repaymentType'];

function isRaw(value: unknown): value is Raw {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Copies the listed keys that are present; everything else in the payload is dropped. */
function pick(source: Raw, keys: readonly string[]): Raw {
  const out: Raw = {};
  keys.forEach(key => { if (source[key] !== undefined) out[key] = source[key]; });
  return out;
}

/** `pick` for a nested object; a non-object is kept as is so the shape check rejects it. */
function pickNested(value: unknown, keys: readonly string[]): unknown {
  return isRaw(value) ? pick(value, keys) : value;
}

function pickList(value: unknown, keys: readonly string[]): unknown {
  return Array.isArray(value) ? value.map(item => pickNested(item, keys)) : value;
}

/**
 * Rebuilds the payload from the known scenario keys only (events included), so a hand-made
 * token cannot smuggle extra properties into localStorage; the name is cut to the form's
 * limit and the id is always fresh. Types are not checked here — `isScenarioShape` is next.
 */
function sanitize(parsed: Raw): Raw {
  const overpayments = Array.isArray(parsed.overpayments)
    ? parsed.overpayments.map((item: unknown) => {
      if (!isRaw(item)) return item;
      const out = pick(item, OVERPAYMENT_KEYS);
      if (item.recurring !== undefined) out.recurring = pickNested(item.recurring, RECURRING_KEYS);
      if (item.adaptive !== undefined) out.adaptive = pickNested(item.adaptive, ADAPTIVE_KEYS);
      return out;
    })
    : parsed.overpayments;
  const out: Raw = {
    ...pick(parsed, SCENARIO_KEYS),
    ratePeriods: pickList(parsed.ratePeriods, RATE_PERIOD_KEYS),
    overpayments,
    refinances: pickList(parsed.refinances, REFINANCE_KEYS),
    id: crypto.randomUUID(),
  };
  if (typeof out.name === 'string') out.name = out.name.slice(0, NAME_MAX_LENGTH);
  if (parsed.bankCalendar !== undefined) out.bankCalendar = pickNested(parsed.bankCalendar, BANK_CALENDAR_KEYS);
  return out;
}

function hasStreams(): boolean {
  return typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';
}

async function pipe(bytes: Uint8Array, transform: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(transform);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** `v1.<base64url(gzip(JSON))>` of the scenario without its id. */
export async function encodeShareToken(scenario: Scenario): Promise<string> {
  if (!hasStreams()) throw new ShareError('unsupported');
  const payload: Partial<Scenario> = { ...scenario };
  delete payload.id;
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  const prefix = scenario.calculationMode === 'bank_calendar' ? CALENDAR_TOKEN_PREFIX : TOKEN_PREFIX;
  return `${prefix}${toBase64Url(await pipe(bytes, new CompressionStream('gzip')))}`;
}

/**
 * The scenario carried by a token, with a fresh id. Throws `ShareError` with `too-long`,
 * `corrupt` (prefix, base64, gzip), `not-a-scenario` (JSON, shape, business rules) or
 * `unsupported` (no Streams API).
 */
export async function decodeShareToken(token: string): Promise<Scenario> {
  if (typeof token !== 'string') throw new ShareError('corrupt');
  if (token.length > MAX_SHARE_PAYLOAD_LENGTH) throw new ShareError('too-long');
  const prefix = token.startsWith(CALENDAR_TOKEN_PREFIX) ? CALENDAR_TOKEN_PREFIX : TOKEN_PREFIX;
  if (!token.startsWith(prefix)) throw new ShareError('corrupt');
  if (!hasStreams()) throw new ShareError('unsupported');
  const packed = fromBase64Url(token.slice(prefix.length));
  let bytes: Uint8Array;
  try {
    bytes = await pipe(packed, new DecompressionStream('gzip'));
  } catch {
    throw new ShareError('corrupt');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new ShareError('not-a-scenario');
  }
  if (!isRaw(parsed)) throw new ShareError('not-a-scenario');
  const candidate: unknown = sanitize(parsed);
  if (!isScenarioShape(candidate) || validateScenario(candidate).length > 0) throw new ShareError('not-a-scenario');
  if (prefix === TOKEN_PREFIX && candidate.calculationMode === 'bank_calendar') throw new ShareError('not-a-scenario');
  return candidate;
}

/**
 * The token inside a pasted link (`…#s=<token>`), a bare fragment (`#s=<token>`) or a bare
 * token; whitespace is trimmed. Null when nothing looks like a token.
 */
export function extractShareToken(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const hashIndex = trimmed.indexOf('#');
  const candidate = hashIndex >= 0
    ? new URLSearchParams(trimmed.slice(hashIndex + 1)).get(SHARE_PARAM)
    : trimmed;
  return candidate && [TOKEN_PREFIX, CALENDAR_TOKEN_PREFIX].some(prefix => candidate.startsWith(prefix) && candidate.length > prefix.length) ? candidate : null;
}

/** `${origin}${pathname}#s=<token>` — the query string is dropped on purpose. */
export function shareUrl(token: string, location: { origin: string; pathname: string }): string {
  return `${location.origin}${location.pathname}#${SHARE_PARAM}=${token}`;
}

